import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Divider,
    Drawer,
    FormControl,
    FormControlLabel,
    FormHelperText,
    FormLabel,
    Grid2,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    TextField,
    Typography,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import * as Yup from "yup";
import AsyncSelect from "react-select/async"; // For dynamic truck number selection
import { useGet, usePost } from "../../../hooks/api-call";
import useCustomToast from "../../../hooks/use-toast";
import { useYupValidationResolver } from "../../../hooks/use-validation-resolver";
import { CloseButton, useBreakpointValue } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { baseURL } from "../../../hooks/helper";
import { useAuth } from "../../../auth/AuthProvider";




const TripDrawer = ({ setIsDrawerOpen, fetchStageWiseCount, handleRefeth }) => {
    const { token } = useAuth();
    const isMobile = useBreakpointValue({ base: true, md: false });

    const headers = {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
    };
    const validationSchema = Yup.object().shape({
        ownerId: Yup.string().required("Please choose a buyer"),
        poNumber: Yup.string().required("Po number is required"),
        pricePerTon: Yup.number()
            .typeError("Must be a number") // Ensures it's a valid number
            .positive("Must be positive") // Must be greater than 0
            .required("Price per ton is required"), // Required field
        paymentTo: Yup.string().required("Payment to is required"),
        truckId: Yup.object().required("Truck No. is required"),
        weight: Yup.number().nullable().positive("Must be positive").notRequired(),
        tdsId: Yup.string().required("TDS is required"),
        driverId: Yup.object()
            .nullable()
            .when("selectDriver", {
                is: "existing",
                then: (schema) => schema.required("Please select a driver"),
                otherwise: (schema) => schema.notRequired(),
            }),
        igstId: Yup.number()
            .nullable()
            .when("selectTaxType", {
                is: "IGST",
                then: (schema) => schema.required("IGST is required"),
                otherwise: (schema) => schema.notRequired(),
            }),
        cgstId: Yup.number()
            .nullable()
            .when("selectTaxType", {
                is: "SGST_CGST",
                then: (schema) => schema.required("CGST is required"),
                otherwise: (schema) => schema.notRequired(),
            }),
        sgstId: Yup.number()
            .nullable()
            .when("selectTaxType", {
                is: "SGST_CGST",
                then: (schema) => schema.required("SGST is required"),
                otherwise: (schema) => schema.notRequired(),
            }),
        // Grouping new driver fields under `newDriver`
        firstName: Yup.string()
            .when("selectDriver", {
                is: "new",
                then: (schema) => schema.required("First Name is required"),
                otherwise: (schema) => schema.notRequired(),
            }),
        lastName: Yup.string()
            .when("selectDriver", {
                is: "new",
                then: (schema) => schema.required("Last Name is required"),
                otherwise: (schema) => schema.notRequired(),
            }),
        contactNo: Yup.string()
            .when("selectDriver", {
                is: "new",
                then: (schema) =>
                    schema
                        .required("Contact Number is required")
                        .matches(/^[0-9]{10}$/, "Must be a valid 10-digit phone number")
                        .test("is-unique", "Contact number already exists", async (value) => {
                            if (!value) return false; // Ensure value is present
                            try {
                                const response = await fetch(`${baseURL}/drivers/checkForDuplicates?contactNumber=${value}`, { headers: headers });
                                const data = await response.json();
                                return !data; // Return true if available, false if already exists
                            } catch (error) {
                                console.error("Error validating contact number:", error);
                                return false; // Assume invalid if API fails
                            }
                        }),
                otherwise: (schema) => schema.notRequired(),
            }),
    });

    //const [truckDetail, setTruckDetail] = useState(null);
    const [selectedBuyerId, setSelectedBuyerId] = useState(null);
    const [selectedPoNumber, setSelectedPoNumber] = useState(null);
    const [poNumbers, setPoNumbers] = useState([]);

    const { responseData: ownernames } = useGet(`buyers`);
    const { responseData: tdsPercantages } = useGet(`getAllTdsPercentage`);
    const { responseData: gstPercantages } = useGet(`getAllGstPercentage`);
    const { fetchData: fetchOrders } = useGet();
    const { postData } = usePost();
    const { showSuccessToast } = useCustomToast();
    const [previousDriver, setPreviousDriver] = useState(null);

    const {
        control,
        handleSubmit,
        setValue,
        reset,
        formState: { errors, isDirty, isValid },
        register,
        getValues, watch, resetField
    } = useForm({
        resolver: useYupValidationResolver(validationSchema),
        mode: "onBlur",  // Validate on blur
        reValidateMode: "onChange", // Revalidate when user types
    });

    const onClose = () => {
        setIsDrawerOpen(false);
        reset({});
    };
    const queryClient = useQueryClient();
    const onSubmit = async (data) => {
        const payload = {
            orderId: data.poNumber,
            truckId: data.truckId.truckId,
            paymentTo: data.paymentTo,
            ravanaNo: data.ravanaNo,
            pricePerTon: data.pricePerTon,
            driverId: data.selectDriver === 'existing' ? data.driverId?.value : data.selectDriver === 'previous' ? previousDriver?.driverId : '',
            tripQuantity: data.weight,
            tdsId: data.tdsId || null,
            driverFirstName: data.firstName,
            driverMiddleName: data.middleName,
            driverLastName: data.lastName,
            driverContactNo: data.contactNo,
            selectDriver: data.selectDriver,
            igstId: data?.igstId || null,
            sgstId: data?.sgstId || null,
            cgstId: data?.cgstId || null
        };
        try {
            await postData(payload, "trip");
            showSuccessToast("Success", `Trip created successfully`);
            onClose();
        } catch (err) {
            console.log("==Error==", err)
        }

        //  fetchStageWiseCount(`trips/stageWiseCount`);
        queryClient.invalidateQueries(['trucks-list']);
    };

    useEffect(() => {
        if (selectedBuyerId) {
            (async () => {
                const resp = await fetchOrders(`buyer/${selectedBuyerId}/orders?buyerId=${selectedBuyerId}`);
                setPoNumbers(resp);
            })();
        } else {
            setPoNumbers([]);
        }
    }, [selectedBuyerId]);

    // Fetch truck numbers based on user input
    const loadTruckOptions = async (inputValue) => {
        if (!inputValue) return [];
        if (inputValue.length < 3) return [];
        const response = await fetchOrders(`${baseURL}/trucks/search?query=${inputValue}`);
        return response.map((truck) => ({
            value: truck.truckId,
            label: truck.truckNumber,
            driver: truck.driver, // Store driver info
            ...truck,
        }));
    };

    const loadDriverOptions = async (inputValue) => {
        if (!inputValue) return [];
        if (inputValue.length < 3) return [];
        const response = await fetchOrders(`${baseURL}/driver/search?query=${inputValue}`);
        return response.map((driver) => ({

            value: driver.driverId,
            label: [
                driver.firstName,
                driver.middleName && driver.middleName.trim() !== "" ? driver.middleName : "",
                driver.lastName && driver.lastName.trim() !== "" ? driver.lastName : ""
            ].filter(Boolean).join(" ") + " - Mob No. " + driver.contactNo,
            ...driver,
        }));
    };

    const handleSetPrivousDriver = (e) => {
        const driver = e?.driver || null;
        setPreviousDriver(driver);
        const driverName = `${driver?.firstName || ''} ${driver?.middleName || ''} ${driver?.lastName || ''} - ${driver?.contactNo || ''}`.trim()
        setValue("previousDriver", driverName);
    }

    const selectedDriverType = watch("selectDriver");
    useEffect(() => {
        if (selectedDriverType === "existing") {
            resetField("driver");  // Clear new driver fields
        } else {
            resetField("firstName"); // Clear existing driver selection
            resetField("middleName");
            resetField("lastName");
            resetField("contactNo");
        }
    }, [selectedDriverType, resetField]);

    const selectedValue = (poNumbers || [])?.find(v => v.orderId === selectedPoNumber);

    useEffect(() => {
        register("igstId");
        register("sgstId");
        register("cgstId");
    }, [register]);

    return (
        <Drawer anchor="right" open={true} onClose={onClose} sx={{ "& .MuiDrawer-paper": { width: isMobile ? "85%" : "60%" } }}>
            <Box sx={{ p: 2 }}>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        // bgcolor: "#1976d2",
                        ///color: "white",
                        p: 2,
                    }}
                >
                    <Typography variant="h6" fontWeight="bold">New Trip</Typography>
                    <IconButton onClick={onClose} sx={{ color: "black" }}>
                        <CloseButton size={'small'} />
                    </IconButton>
                </Box>
                <Divider sx={{ my: 2 }} />
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Typography
                        variant="subtitle1"
                        sx={{
                            fontWeight: "bold",
                            color: "#333",
                            p: 1,
                            mb: 2,
                            borderLeft: "4px solid #1976d2",
                            bgcolor: "#f0f0f0",
                            pl: 2,
                        }}
                    >
                        Trip Truck Information
                    </Typography>
                    <Grid2 container spacing={2} pb={2}>
                        <Grid2 size={6} sm={6}>
                            <FormControl fullWidth>
                                <FormLabel>Truck No.</FormLabel>
                                <Controller
                                    name="truckId"
                                    control={control}
                                    render={({ field }) => (
                                        <AsyncSelect
                                            isClearable
                                            required
                                            placeholder='Enter Truck No.'
                                            classNamePrefix="mySelect"
                                            {...field}
                                            cacheOptions
                                            loadOptions={loadTruckOptions}
                                            defaultOptions
                                            onChange={(e) => {
                                                field.onChange(e);
                                                handleSetPrivousDriver(e)
                                                // setTruckDetail(e);
                                            }}
                                            styles={{
                                                menuPortal: (base) => ({ ...base, zIndex: 9999 }), // Ensures it stays on top
                                                menu: (base) => ({ ...base, zIndex: 9999 }),
                                            }}
                                        />
                                    )}
                                />
                                {errors.truckId && <Typography color="error">{errors.truckId.message}</Typography>}
                            </FormControl>
                        </Grid2>
                        <Grid2 size={6} sm={6}>
                            <FormControl fullWidth error={!!errors.paymentTo}>
                                <FormLabel>Payment To</FormLabel>
                                <Controller
                                    name="paymentTo"
                                    control={control}
                                    defaultValue={"aggregator"}
                                    render={({ field }) => (
                                        <RadioGroup {...field} row>
                                            <FormControlLabel value="aggregator" control={<Radio />} label="Aggregator" />
                                            <FormControlLabel value="owner" control={<Radio />} label="Owner" />
                                        </RadioGroup>
                                    )}
                                />
                                {errors.paymentTo && <Typography color="error">{errors.paymentTo.message}</Typography>}
                            </FormControl>
                        </Grid2>
                    </Grid2>
                    <Grid2 container spacing={2}>
                        <Grid2 container width={'100%'}>
                            <Grid2 width={'50%'} display={'flex'} flexDirection={'column'}>
                                
                                <Grid2 xs={12} marginTop={2}>
                                    <FormControl fullWidth error={!!errors.weight}>
                                        <InputLabel id="level-label">Tds</InputLabel>
                                        <Controller
                                            name="tdsId"
                                            id="tdsId"
                                            control={control}
                                            render={({ field }) => (
                                                <Select labelId="level-label" {...field} label="Tds">
                                                    {tdsPercantages?.map((option) => {
                                                        if (!option.value == 0) {
                                                            return (<MenuItem key={option.tdsPercentageId} value={option.tdsPercentageId}>
                                                                {option.value}
                                                            </MenuItem>)
                                                        }
                                                    })}
                                                </Select>
                                            )}
                                        />
                                        <FormHelperText error={true}>{errors.tdsId?.message}</FormHelperText>
                                    </FormControl>
                                </Grid2>
                                <Grid2 xs={12} md={4} marginTop={2}>
                                    {/* Tax Type Radios */}
                                    <Controller
                                        name="selectTaxType"
                                        control={control}
                                        defaultValue={'IGST'}
                                        render={({ field }) => (
                                            <FormControl component="fieldset" >
                                                <FormLabel>Tax Type</FormLabel>
                                                <RadioGroup
                                                    row
                                                    {...field}
                                                    onChange={(e) => {
                                                        field.onChange(e);
                                                        setValue("igstId", null);
                                                        setValue("cgstId", null);
                                                        setValue("sgstId", null);
                                                    }}
                                                >
                                                    <FormControlLabel value="IGST" control={<Radio />} label="IGST" />
                                                    <FormControlLabel value="SGST_CGST" control={<Radio />} label="SGST & CGST" />
                                                </RadioGroup>
                                            </FormControl>
                                        )}
                                    />
                                </Grid2>
                                <Grid2 display={'flex'} marginTop={2}>
                                    {watch("selectTaxType") === "IGST" ? (
                                        <FormControl style={{ width: '100%' }} error={!!errors.igstId}>
                                            <InputLabel id="igstId-label">IGST</InputLabel>
                                            <Controller
                                                name="igstId"
                                                id="igstId"
                                                defaultValue={''}
                                                control={control}
                                                render={({ field }) => (
                                                    <Select labelId="igstId-label"  {...field} label="IGST">
                                                        {gstPercantages?.map((option) => {
                                                            if (!option.value == 0) {
                                                                return <MenuItem key={option.gstPercentageId} value={option.gstPercentageId}>
                                                                    {option.value}
                                                                </MenuItem>
                                                            }
                                                        })}
                                                    </Select>
                                                )}
                                            />
                                        </FormControl>
                                    ) : (
                                        <Grid2 width={'100%'} display={'flex'}>
                                            <FormControl style={{ width: '100%' }} error={!!errors.gstId}>
                                                <InputLabel id="sgstId-label" shrink={!!watch('sgstId')}>SGST</InputLabel>
                                                <Controller
                                                    name="sgstId"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Select
                                                            labelId="sgstId-label"
                                                            label="SGST"
                                                            {...field}
                                                            onChange={(e) => {
                                                                field.onChange(e); 
                                                                setValue('cgstId', e.target.value);
                                                            }}
                                                        >
                                                            {gstPercantages?.map((option) =>
                                                                (option.value !== 0 && option.value !== 18) ? (
                                                                    <MenuItem key={option.gstPercentageId} value={option.gstPercentageId}>
                                                                        {option.value}
                                                                    </MenuItem>
                                                                ) : null
                                                            )}
                                                        </Select>
                                                    )}
                                                />
                                            </FormControl>
                                            <FormControl style={{ width: '100%', marginLeft: '10px' }} error={!!errors.gstId}>
                                                <InputLabel id="cgstId-label" shrink={!!watch('cgstId')}>CGST</InputLabel>
                                                <Controller
                                                    name="cgstId"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Select
                                                            labelId="cgstId-label"
                                                            label="CGST"
                                                            {...field}
                                                            onChange={(e) => {
                                                                field.onChange(e); // update cgstId
                                                                setValue('sgstId', e.target.value); // mirror to sgstId
                                                            }}
                                                        >
                                                            {gstPercantages?.map((option) =>
                                                                (option.value !== 0 && option.value !== 18) ? (
                                                                    <MenuItem key={option.gstPercentageId} value={option.gstPercentageId}>
                                                                        {option.value}
                                                                    </MenuItem>
                                                                ) : null
                                                            )}
                                                        </Select>
                                                    )}
                                                />
                                            </FormControl>
                                        </Grid2>

                                    )}
                                    {errors.gstPercentage && (
                                        <Typography color="error">{errors.gstPercentage.message}</Typography>
                                    )}
                                </Grid2>
                            </Grid2>
                            <Grid2 size={4} sm={4} marginTop={2}>
                            <FormControl fullWidth required>
                                <Controller
                                    name="pricePerTon"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField label="Trip Price per ton" type="number" {...field}> </TextField>
                                    )}
                                />
                                {errors.pricePerTon && <Typography color="error">{errors.pricePerTon.message}</Typography>}
                            </FormControl>
                        </Grid2>
                        </Grid2>
                    </Grid2>
                    <div style={{ marginTop: "20px" }}>
                        <Typography
                            variant="subtitle1"
                            sx={{
                                fontWeight: "bold",
                                color: "#333",
                                p: 1,
                                mb: 2,
                                borderLeft: "4px solid #1976d2",
                                bgcolor: "#f0f0f0",
                                pl: 2,
                            }}
                        >
                            Truck Booking
                        </Typography>
                    </div>
                    <Grid2 container spacing={2} pb={2}>
                        <Grid2 width={'100%'} size={6} sm={6}>
                            <FormControl fullWidth >
                                <FormLabel>Select Driver</FormLabel>
                                <Controller
                                    id="selectDriver"
                                    name="selectDriver"
                                    control={control}
                                    defaultValue={"existing"}
                                    render={({ field }) => (
                                        <RadioGroup {...field} row>
                                            {previousDriver && <FormControlLabel label="Previous Driver" control={<Radio />} value="previous" />}
                                            <FormControlLabel label="Registered Driver" control={<Radio />} value="existing" />
                                            <FormControlLabel label="New Driver" control={<Radio />} value="new" />
                                        </RadioGroup>
                                    )}
                                />
                                {errors.selectDriver && <Typography color="error">{errors.selectDriver.message}</Typography>}
                            </FormControl>
                        </Grid2>
                    </Grid2>
                    <>
                        {selectedDriverType === "existing" && (
                            <Grid2 container spacing={2} pb={2}>
                                <Grid2 size={6} sm={6}>
                                    <FormControl fullWidth>
                                        <FormLabel>Driver</FormLabel>
                                        <Controller
                                            name="driverId"
                                            control={control}
                                            render={({ field }) => (
                                                <AsyncSelect
                                                    required
                                                    placeholder="Enter Driver name or Mob no."
                                                    isClearable
                                                    classNamePrefix="mySelect"
                                                    {...field}
                                                    cacheOptions
                                                    loadOptions={loadDriverOptions}
                                                    defaultOptions
                                                    onChange={(e) => field.onChange(e)}
                                                    styles={{
                                                        menuPortal: (base) => ({ ...base, zIndex: 9999 }), // Ensures it stays on top
                                                        menu: (base) => ({ ...base, zIndex: 9999 }),
                                                    }}
                                                />
                                            )}
                                        />
                                        {errors.driverId && <Typography color="error">{errors.driverId.message}</Typography>}
                                    </FormControl>
                                </Grid2>
                            </Grid2>
                        )}
                        {selectedDriverType === "previous" && (
                            <Grid2 container spacing={2} pb={2}>
                                <Grid2 size={6} sm={6}>
                                    <FormControl fullWidth>
                                        <FormLabel>Driver</FormLabel>
                                        <Controller
                                            name="previousDriver"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    label="Enter Driver name or Mob no."
                                                    type="text" style={{ marginTop: '10px' }}
                                                    InputProps={{ readOnly: true }} {...field}> </TextField>
                                            )}
                                        />
                                    </FormControl>
                                </Grid2>
                            </Grid2>
                        )}
                        {selectedDriverType === "new" && (
                            <Grid2 container spacing={2} pb={2}>
                                <Grid2 size={6} sm={6}>
                                    <FormControl fullWidth>
                                        <Controller
                                            name="firstName"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    fullWidth
                                                    label="First Name"
                                                />
                                            )}
                                        />
                                        <FormHelperText error={true}>{errors.firstName?.message}</FormHelperText>
                                    </FormControl>
                                </Grid2>
                                <Grid2 size={6} sm={6}>
                                    <FormControl fullWidth>
                                        <Controller
                                            name="middleName"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField {...field} fullWidth label="Middle Name (Optional)" />
                                            )}
                                        />
                                    </FormControl>
                                </Grid2>
                                <Grid2 size={6} sm={6}>
                                    <FormControl fullWidth >
                                        <Controller
                                            name="lastName"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    fullWidth
                                                    label="Last Name"
                                                />
                                            )}
                                        />
                                        <FormHelperText error={true}>{errors.lastName?.message}</FormHelperText>
                                    </FormControl>
                                </Grid2>
                                <Grid2 size={6} sm={6}>
                                    <FormControl fullWidth>
                                        <Controller
                                            name="contactNo"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    fullWidth
                                                    label="Mobile Number"
                                                    error={!!errors.contactNo}
                                                    helperText={errors.contactNo?.message}
                                                />
                                            )}
                                        />
                                    </FormControl>
                                </Grid2>
                            </Grid2>
                        )}
                    </>
                    <div style={{ marginTop: "20px" }}>
                        <Typography
                            variant="subtitle1"
                            sx={{
                                fontWeight: "bold",
                                color: "#333",
                                p: 1,
                                mb: 2,
                                borderLeft: "4px solid #1976d2",
                                bgcolor: "#f0f0f0",
                                pl: 2,
                            }}
                        >
                            Order Information
                        </Typography>
                    </div>
                    <Grid2 container spacing={2}>
                        <Grid2 item size={4} sm={4}>
                            <FormControl fullWidth error={!!errors.ownerId}>
                                <InputLabel>Select Buyer</InputLabel>
                                <Controller
                                    required
                                    label="Select Buyer"
                                    name="ownerId"
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            label="Select Buyer"
                                            {...field}
                                            required
                                            onChange={(e) => {
                                                field.onChange(e);
                                                setSelectedBuyerId(e.target.value);
                                                setValue("poNumber", "");

                                            }}
                                        >
                                            {ownernames?.map((owner) => (
                                                <MenuItem key={owner.buyerId} value={owner.buyerId}>
                                                    {owner.buyerName}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    )}
                                />
                                {errors.ownerId && <Typography color="error">{errors.ownerId.message}</Typography>}
                            </FormControl>
                        </Grid2>
                        <Grid2 size={4} sm={4}>
                            <FormControl fullWidth error={!!errors.poNumber}>
                                <InputLabel>Po Number</InputLabel>
                                <Controller
                                    name="poNumber"
                                    control={control}
                                    render={({ field }) => (
                                        <Select {...field} 
                                        disabled={!poNumbers.length}
                                        onChange={(e) => {
                                                field.onChange(e);
                                                setSelectedPoNumber(e.target.value);
                                            }}

                                         label="Po Number" required>
                                            {poNumbers?.map((po) => (
                                                <MenuItem key={po.orderId} value={po.orderId}>
                                                    {po.poNumber}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    )}
                                />
                                {errors.poNumber && <Typography color="error">{errors.poNumber.message}</Typography>}
                            </FormControl>
                        </Grid2>
                        <Grid2 size={4} sm={4}>
                            <TextField
                                fullWidth
                                label="PO Type"
                                value={selectedValue?.poType || ""}
                                InputProps={{ readOnly: true }}
                                required
                            />
                        </Grid2>
                        <Grid2 size={4} sm={4}>
                            <TextField
                                fullWidth
                                label="Mineral Price Per Ton"
                                value={selectedValue?.mineralPpt || ""}
                                InputProps={{ readOnly: true }}
                                required
                            />
                        </Grid2>
                        <Grid2 size={4} sm={4}>
                            <TextField
                                fullWidth
                                label="Freight Price Per Ton"
                                value={selectedValue?.freightPpt || ""}
                                InputProps={{ readOnly: true }}
                                required
                            />
                        </Grid2>
                    </Grid2>

                    <Box display="flex" justifyContent="flex-end" mt={3}>
                        <Button type="submit" variant="contained" color="primary" disabled={!isDirty || !isValid}>
                            Submit
                        </Button>
                    </Box>
                </form>
            </Box>
        </Drawer>
    );
};

export default TripDrawer;
